import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchApi } from '../lib/api';
import CalendlyBooking from '../components/event/CalendlyBooking';
import NDAInbox from '../components/event/NDAInbox';
import PitchSchedule from '../components/event/PitchSchedule';

export default function CompanyEventDashboard({ eventId, onBack }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [companyHour, setCompanyHour] = useState(null);
  const [slots, setSlots] = useState([]);
  const [trends, setTrends] = useState([]);
  const [connections, setConnections] = useState([]);
  const [ndaMap, setNdaMap] = useState({});
  const [matchScores, setMatchScores] = useState({});
  const [registration, setRegistration] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventRes, slotsRes, trendsRes] = await Promise.all([
        fetchApi(`/api/events/${eventId}`),
        fetchApi(`/api/events/${eventId}/my-slots`),
        fetchApi('/api/trends').catch(() => ({ trends: [] })),
      ]);

      setEvent(eventRes.event || eventRes);
      setSlots(slotsRes.slots || []);
      setTrends((trendsRes.trends || []).slice(0, 5));

      // Extract company hour from slots response or fetch separately
      if (slotsRes.slots?.[0]?.company_zoom_link !== undefined) {
        // Slots exist, hour is booked
        const firstSlot = slotsRes.slots[0];
        if (firstSlot) {
          setCompanyHour({
            hour_start: firstSlot.slot_start,
            hour_end: new Date(new Date(firstSlot.slot_start).getTime() + 60 * 60 * 1000).toISOString(),
            zoom_link: firstSlot.company_zoom_link,
          });
        }
      }

      // Check for company hour directly
      try {
        const hourRes = await fetchApi(`/api/events/${eventId}/company-hour`);
        if (hourRes.hour) setCompanyHour(hourRes.hour);
      } catch {
        // Endpoint may not exist yet or no hour booked
      }

      // Get event registration for sponsorship tier
      try {
        const regRes = await fetchApi(`/api/events/${eventId}/my-registration`);
        if (regRes.registration) setRegistration(regRes.registration);
      } catch {
        // May not exist
      }

      // Fetch connections for NDA inbox
      try {
        const connRes = await fetchApi('/api/connections/incoming');
        const eventConns = (connRes.connections || []).filter(c => c.status === 'approved');
        setConnections(eventConns);

        // Fetch NDA records and match scores for each connection
        const ndaResults = {};
        const scores = {};
        await Promise.all(eventConns.map(async (conn) => {
          try {
            const ndaRes = await fetchApi(`/api/nda/${conn.id}`);
            ndaResults[conn.id] = ndaRes.records || [];
          } catch { ndaResults[conn.id] = []; }

          // Match scores
          if (conn.inventor_id) {
            try {
              const scoreRes = await fetchApi(`/api/match-score?inventor_id=${conn.inventor_id}&company_id=${profile.id}`);
              scores[conn.inventor_id] = scoreRes.score || 0;
            } catch { scores[conn.inventor_id] = 0; }
          }
        }));
        setNdaMap(ndaResults);
        setMatchScores(scores);
      } catch {
        setConnections([]);
      }
    } catch (err) {
      console.error('CompanyEventDashboard load error:', err);
      setError(err.message);
    }
    setLoading(false);
  }, [eventId, profile?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center mx-auto mb-3">
            <span className="text-white font-bold text-sm font-logo">PV</span>
          </div>
          <p className="text-sm text-gray-400">Loading your event dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center glass p-8 max-w-md">
          <p className="text-vault-error text-sm font-semibold mb-2">Failed to load event dashboard</p>
          <p className="text-xs text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-5 py-2 rounded-btn text-sm font-semibold text-white"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Compute event status
  const now = new Date();
  const startDate = event?.start_date ? new Date(event.start_date) : null;
  const endDate = event?.end_date ? new Date(event.end_date) : null;
  let eventStatus = 'upcoming';
  if (endDate && now > endDate) eventStatus = 'completed';
  else if (startDate && now >= startDate && endDate && now <= endDate) eventStatus = 'live';

  // Format date range
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    const opts = { month: 'long', day: 'numeric' };
    const startStr = startDate.toLocaleDateString('en-US', opts);
    const endStr = endDate.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDate.toLocaleDateString('en-US', { month: 'long' })} ${startDate.getDate()}–${endDate.getDate()}, ${endDate.getFullYear()}`;
    }
    return `${startStr} – ${endStr}`;
  };

  const statusBadge = {
    upcoming: { bg: 'bg-blue-100 text-blue-700', label: 'Upcoming' },
    live: { bg: 'bg-emerald-100 text-emerald-700', label: 'Live' },
    completed: { bg: 'bg-gray-100 text-gray-500', label: 'Completed' },
  };

  const tierBadge = registration?.sponsorship_tier === 'premium_500'
    ? { bg: 'bg-gradient-to-r from-vault-gold to-amber-500 text-white', label: 'Premium Sponsor' }
    : registration?.sponsorship_tier === 'standard_250'
    ? { bg: 'bg-gray-200 text-gray-700', label: 'Standard Sponsor' }
    : null;

  const trendDirectionBadge = {
    rising: { cls: 'bg-emerald-100 text-emerald-700', label: '↑ Rising' },
    peaking: { cls: 'bg-amber-100 text-amber-700', label: '◆ Peaking' },
    declining: { cls: 'bg-red-100 text-red-600', label: '↓ Declining' },
    oversaturated: { cls: 'bg-gray-200 text-gray-600', label: '⚠ Saturated' },
  };

  return (
    <div>
      {/* Back button */}
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-vault-gold mb-4 transition-colors">
          ← Back to Events
        </button>
      )}

      {/* EVENT HEADER */}
      <div className="glass p-7 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-[28px] text-vault-navy leading-tight">
              {event?.event_name || event?.name || 'Event'}
            </h1>
            {formatDateRange() && (
              <p className="text-sm text-gray-400 mt-1">{formatDateRange()}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${statusBadge[eventStatus].bg}`}>
              {eventStatus === 'live' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />}
              {statusBadge[eventStatus].label}
            </span>
            {tierBadge && (
              <span className={`px-3 py-1 rounded-full text-[11px] font-semibold ${tierBadge.bg}`}>
                {tierBadge.label}
              </span>
            )}
          </div>
        </div>
        {eventStatus === 'live' && (
          <div className="flex items-center gap-2 mt-3 text-sm text-emerald-600">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Event is live now
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN — Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* CALENDLY BOOKING */}
          <CalendlyBooking companyHour={companyHour} />

          {/* NDA INBOX */}
          <NDAInbox
            connections={connections}
            ndaMap={ndaMap}
            matchScores={matchScores}
            onRefresh={loadData}
          />

          {/* MY SLOTS */}
          <PitchSchedule companyHour={companyHour} slots={slots} />
        </div>

        {/* RIGHT COLUMN — Trends sidebar */}
        <div className="space-y-4">
          <div className="glass p-5">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-heading text-lg text-vault-navy">Industry Trends</h2>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                Teaser
              </span>
            </div>

            {trends.length === 0 ? (
              <p className="text-sm text-gray-400">No trends available yet.</p>
            ) : (
              <div className="space-y-3">
                {trends.map(trend => {
                  const dir = trendDirectionBadge[trend.trend_direction] || trendDirectionBadge.rising;
                  const summary = trend.trend_summary?.split('.')[0] || '';
                  return (
                    <div key={trend.id} className="border border-vault-border rounded-xl p-3.5">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h3 className="text-sm font-semibold text-vault-navy leading-snug">{trend.trend_topic}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${dir.cls}`}>
                          {dir.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed mb-2">{summary}.</p>
                      {trend.trend_category && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {trend.trend_category}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upgrade card */}
          <div className="glass p-5 border-2 border-dashed border-vault-gold/30">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📈</span>
              <h3 className="text-sm font-semibold text-vault-navy">Trend Intelligence</h3>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Full trend analysis, AI scanning, and market saturation scores are available from the Trends page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
